/**
 * Comment store - manages comments and notifications state
 */

import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import {
  commentsApi,
  notificationsApi,
  type Comment,
  type Notification,
  type Attachment,
} from '@services/api';

interface CommentState {
  // Comments state
  comments: Record<string, Comment[]>; // taskId -> comments
  commentsByTask: Record<string, { loading: boolean; error: string | null }>;

  // Notifications state
  notifications: Notification[];
  unreadCount: number;
  notificationsLoading: boolean;
  notificationsError: string | null;

  // Comment actions
  fetchComments: (taskId: string) => Promise<void>;
  createComment: (taskId: string, content: string, parentId?: string) => Promise<Comment>;
  updateComment: (commentId: string, content: string) => Promise<Comment>;
  deleteComment: (taskId: string, commentId: string) => Promise<void>;

  // Reaction actions
  addReaction: (taskId: string, commentId: string, emoji: string) => Promise<void>;
  removeReaction: (taskId: string, commentId: string, emoji: string) => Promise<void>;

  // Attachment actions
  initiateUpload: (
    taskId: string,
    commentId: string | null,
    file: { filename: string; contentType: string; sizeBytes: number }
  ) => Promise<{ attachmentId: string; uploadUrl: string }>;
  confirmUpload: (attachmentId: string, fileHash?: string) => Promise<Attachment>;

  // Notification actions
  fetchNotifications: (unreadOnly?: boolean) => Promise<void>;
  fetchUnreadCount: () => Promise<void>;
  markAsRead: (notificationIds: string[]) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  archiveNotification: (notificationId: string) => Promise<void>;
  archiveAllNotifications: () => Promise<void>;
  clickNotification: (notificationId: string) => Promise<string | null>;

  // UI actions
  setComments: (taskId: string, comments: Comment[]) => void;
  addComment: (taskId: string, comment: Comment) => void;
  updateCommentInStore: (taskId: string, comment: Comment) => void;
  removeComment: (taskId: string, commentId: string) => void;
  reset: () => void;
}

const initialState = {
  comments: {},
  commentsByTask: {},
  notifications: [],
  unreadCount: 0,
  notificationsLoading: false,
  notificationsError: null,
};

export const useCommentStore = create<CommentState>()(
  immer((set, _get) => ({
    ...initialState,

    // =========================================================================
    // Comment Actions
    // =========================================================================

    fetchComments: async (taskId) => {
      set((state) => {
        state.commentsByTask[taskId] = { loading: true, error: null };
      });

      try {
        const comments = await commentsApi.getTaskComments(taskId);
        set((state) => {
          state.comments[taskId] = comments;
          state.commentsByTask[taskId] = { loading: false, error: null };
        });
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Failed to load comments';
        set((state) => {
          state.commentsByTask[taskId] = { loading: false, error: message };
        });
      }
    },

    createComment: async (taskId, content, parentId) => {
      const request = parentId ? { content, parentId } : { content };
      const comment = await commentsApi.createComment(taskId, request);
      set((state) => {
        if (!state.comments[taskId]) {
          state.comments[taskId] = [];
        }
        state.comments[taskId].push(comment);
      });
      return comment;
    },

    updateComment: async (commentId, content) => {
      const comment = await commentsApi.updateComment(commentId, { content });
      // Find and update in the correct task's comments
      set((state) => {
        for (const taskId in state.comments) {
          const taskComments = state.comments[taskId];
          if (taskComments) {
            const idx = taskComments.findIndex((c) => c.id === commentId);
            if (idx !== -1) {
              taskComments[idx] = comment;
              break;
            }
          }
        }
      });
      return comment;
    },

    deleteComment: async (taskId, commentId) => {
      await commentsApi.deleteComment(commentId);
      set((state) => {
        if (state.comments[taskId]) {
          state.comments[taskId] = state.comments[taskId].filter((c) => c.id !== commentId);
        }
      });
    },

    // =========================================================================
    // Reaction Actions
    // =========================================================================

    addReaction: async (taskId, commentId, emoji) => {
      const updatedComment = await commentsApi.addReaction(commentId, emoji);
      set((state) => {
        if (state.comments[taskId]) {
          const idx = state.comments[taskId].findIndex((c) => c.id === commentId);
          if (idx !== -1) {
            state.comments[taskId][idx] = updatedComment;
          }
        }
      });
    },

    removeReaction: async (taskId, commentId, emoji) => {
      const updatedComment = await commentsApi.removeReaction(commentId, emoji);
      set((state) => {
        if (state.comments[taskId]) {
          const idx = state.comments[taskId].findIndex((c) => c.id === commentId);
          if (idx !== -1) {
            state.comments[taskId][idx] = updatedComment;
          }
        }
      });
    },

    // =========================================================================
    // Attachment Actions
    // =========================================================================

    initiateUpload: async (taskId, commentId, file) => {
      if (commentId) {
        return await commentsApi.initiateCommentAttachmentUpload(commentId, file);
      } else {
        return await commentsApi.initiateTaskAttachmentUpload(taskId, file);
      }
    },

    confirmUpload: async (attachmentId, fileHash) => {
      return await commentsApi.confirmAttachmentUpload(attachmentId, fileHash);
    },

    // =========================================================================
    // Notification Actions
    // =========================================================================

    fetchNotifications: async (unreadOnly = false) => {
      set((state) => {
        state.notificationsLoading = true;
        state.notificationsError = null;
      });

      try {
        const response = await notificationsApi.getNotifications(unreadOnly);
        set((state) => {
          state.notifications = response.notifications;
          state.unreadCount = response.unreadCount;
          state.notificationsLoading = false;
        });
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Failed to load notifications';
        set((state) => {
          state.notificationsLoading = false;
          state.notificationsError = message;
        });
      }
    },

    fetchUnreadCount: async () => {
      try {
        const { count } = await notificationsApi.getUnreadCount();
        set((state) => {
          state.unreadCount = count;
        });
      } catch (error) {
        console.error('Failed to fetch unread count:', error);
      }
    },

    markAsRead: async (notificationIds) => {
      await notificationsApi.markAsRead(notificationIds);
      set((state) => {
        state.notifications = state.notifications.map((n) =>
          notificationIds.includes(n.id) ? { ...n, isRead: true, readAt: new Date().toISOString() } : n
        );
        state.unreadCount = Math.max(0, state.unreadCount - notificationIds.length);
      });
    },

    markAllAsRead: async () => {
      await notificationsApi.markAllAsRead();
      set((state) => {
        state.notifications = state.notifications.map((n) => ({
          ...n,
          isRead: true,
          readAt: new Date().toISOString(),
        }));
        state.unreadCount = 0;
      });
    },

    archiveNotification: async (notificationId) => {
      await notificationsApi.archiveNotification(notificationId);
      set((state) => {
        state.notifications = state.notifications.filter((n) => n.id !== notificationId);
      });
    },

    archiveAllNotifications: async () => {
      await notificationsApi.archiveAll();
      set((state) => {
        state.notifications = [];
      });
    },

    clickNotification: async (notificationId) => {
      const notification = await notificationsApi.clickNotification(notificationId);
      set((state) => {
        const idx = state.notifications.findIndex((n) => n.id === notificationId);
        if (idx !== -1) {
          const existing = state.notifications[idx];
          state.notifications[idx] = notification;
          if (existing && !existing.isRead) {
            state.unreadCount = Math.max(0, state.unreadCount - 1);
          }
        }
      });
      return notification.actionUrl ?? null;
    },

    // =========================================================================
    // UI Actions
    // =========================================================================

    setComments: (taskId, comments) => {
      set((state) => {
        state.comments[taskId] = comments;
      });
    },

    addComment: (taskId, comment) => {
      set((state) => {
        if (!state.comments[taskId]) {
          state.comments[taskId] = [];
        }
        state.comments[taskId].push(comment);
      });
    },

    updateCommentInStore: (taskId, comment) => {
      set((state) => {
        if (state.comments[taskId]) {
          const idx = state.comments[taskId].findIndex((c) => c.id === comment.id);
          if (idx !== -1) {
            state.comments[taskId][idx] = comment;
          }
        }
      });
    },

    removeComment: (taskId, commentId) => {
      set((state) => {
        if (state.comments[taskId]) {
          state.comments[taskId] = state.comments[taskId].filter((c) => c.id !== commentId);
        }
      });
    },

    reset: () => {
      set(initialState);
    },
  }))
);

export default useCommentStore;
